import classnames from 'classnames';
import React, { cloneElement, ReactElement, ReactNode } from 'react';

import { Icon, IconProps } from '../icon/Icon';
import { TooltipProps } from '../tooltip/Tooltip';
import styles from './RadioGroup.module.scss';

export interface RadioGroupItem {
  id: string;
  testId?: string;
  label: ReactNode;
  tooltip?: ReactElement<Omit<TooltipProps, 'children'>>;
  error?: boolean;
  icon?: IconProps;
  disabled?: boolean;
  locked?: boolean;
}

export interface RadioGroupProps {
  items: RadioGroupItem[];
  activeId?: string;
  direction?: 'column' | 'row';
  size?: 'medium' | 'small';
  groupLabel?: string;
  className?: string;
  onClick?: (id: string, idx: number) => void;
  onChange?: (id: string, idx: number) => void;
}

export const RadioGroup = ({
  className,
  items,
  activeId,
  groupLabel,
  onChange,
  onClick,
  direction = 'column',
  size = 'medium',
}: RadioGroupProps) => {
  return (
    <div
      className={classnames(styles.container, className)}
      data-radio-container
    >
      {groupLabel && <span className={styles.groupLabel}>{groupLabel}</span>}
      <div
        className={
          direction === 'column' ? styles.groupColumn : styles.groupRow
        }
        role="radiogroup"
      >
        {items.map((item, idx) => {
          const isLocked = !!item.locked;
          const isDisabled = !!item.disabled;
          const isError = !!item.error;
          const isInactive = isDisabled || isLocked;

          const TooltipWithLabel = item.tooltip
            ? cloneElement(
                item.tooltip,
                {
                  placement: item.tooltip.props.placement ?? 'top-start',
                  inPortal: true,
                  ...item.tooltip.props,
                },
                <span
                  className={classnames(styles.label, styles.labelWithTooltip)}
                >
                  {item.label}
                </span>
              )
            : null;

          const testId = item.testId ?? item.id;

          return (
            <label
              key={item.id}
              className={classnames(
                styles.radioLabel,
                size === 'medium'
                  ? styles.radioLabelMedium
                  : styles.radioLabelSmall,
                {
                  [styles.radioLabelDisabled]: isDisabled,
                  [styles.radioLabelInactive]: isInactive,
                  [styles.radioLabelEnabled]: !isDisabled,
                  [styles.radioLabelLocked]: isLocked,
                }
              )}
              data-testid={testId}
              data-radio-label
              htmlFor={item.id}
            >
              {isLocked && (
                <Icon
                  className={styles.lockIcon}
                  iconName="LockLight"
                  size={20}
                />
              )}
              <input
                type="radio"
                className={classnames(styles.radioInput, {
                  [styles.radioInputDisabled]: isInactive,
                  [styles.radioInputError]: !isInactive && isError,
                  [styles.radioInputLocked]: isLocked,
                  [styles.radioInputUnlocked]: !isLocked,
                })}
                data-testid={`radio-${testId}`}
                data-radio-input
                id={item.id}
                value={item.id}
                disabled={isDisabled}
                onChange={() => onChange?.(item.id, idx)}
                onClick={onClick ? () => onClick(item.id, idx) : undefined}
                checked={item.id === activeId}
              />
              <div className={styles.labelContainer}>
                {item.icon && (
                  <Icon className={styles.styledIcon} {...item.icon} />
                )}
                {TooltipWithLabel ?? (
                  <span className={styles.label}>{item.label}</span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};
